import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Terms from "./terms";
import Privacy from "./privacy_policy";
import "./AuthPage.css";
import AuthLeftPanel from "./component/AuthLeftPanel";

const isTokenValid = (tokenKey = "access_token") => {
  const token = localStorage.getItem(tokenKey);
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (payload.exp * 1000 <= Date.now()) {
      localStorage.removeItem(tokenKey);
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

const Signup = () => {
  const navigate = useNavigate();
  // Invited users arrive via /register?invite={token}; the backend already
  // holds their email/tenant, so they only set name + password here.
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite");
  const [formData, setFormData] = useState({
    company_name: "",
    first_name: "",
    last_name: "",
    email: "",
    password: "",
  });
  const [isChecked, setIsChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [signupEmail, setSignupEmail] = useState("");

  useEffect(() => {
    if (isTokenValid("access_token")) {
      navigate("/chat", { replace: true });
    }
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Password validation function
  const validatePassword = (password) => {
    const minLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return {
      isValid: minLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar,
      errors: [
        !minLength && "Password must be at least 8 characters long.",
        !hasUpperCase && "Password must contain at least one uppercase letter.",
        !hasLowerCase && "Password must contain at least one lowercase letter.",
        !hasNumber && "Password must contain at least one number.",
        !hasSpecialChar && "Password must contain at least one special character (e.g., !@#$%).",
      ].filter(Boolean),
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate password
    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      toast.error("Password should be at least 8 digits and contain an uppercase, a lower case, a special character and a number.");
      return;
    }

    if (!isChecked) {
      toast.error("You must agree to the terms and privacy policy.");
      return;
    }

    setLoading(true);
    const url = `${process.env.REACT_APP_BASE_URL}/register/`;
    // Invite mode: the backend takes email/company from the token's
    // pre-created user, so only send what the user actually entered.
    const body = JSON.stringify(
      inviteToken
        ? {
            invite: inviteToken,
            first_name: formData.first_name,
            last_name: formData.last_name,
            password: formData.password,
          }
        : formData
    );

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
      });
      const data = await response.json();

      if (response.ok) {
        setSignupEmail(formData.email);
        setSignupSuccess(true);
      } else if (data.error === "invalid_invite") {
        toast.error("This invitation is invalid or expired — ask your admin to re-invite you.");
      } else {
        const errorMessage = data.error || Object.values(data).flat().join(" ") || "Signup failed. Please try again.";
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error(error.message || "An error occurred. Please try again.");
    }

    setLoading(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  const openModal = (content) => {
    setModalContent(content);
  };

  const closeModal = () => {
    setModalContent(null);
  };

  return (
    <>
      <nav className="auth-nav">
        <Link to="/" className="auth-nav-logo">
          <img
            src="/StratusEdgeFinal_whiteupscaled.png"
            alt="Edge AI"
            className="auth-nav-logo-img"
          />
        </Link>
      </nav>
      <div className="auth-split">
      <AuthLeftPanel variant="register" />
      <div className="auth-split-border" aria-hidden="true" />

      <div className="auth-split-right">
        <div className="auth-form-shell auth-form-animate auth-form-signup">
          <h2 className="auth-heading">
            {inviteToken ? "You've been invited" : "Create your account"}
          </h2>
          <p className="auth-subheading">
            {inviteToken
              ? "Finish setting up your account."
              : "Get started with Edge AI."}
          </p>

          {signupSuccess && inviteToken ? (
            <div className="auth-success-flow">
              <div className="auth-success-steps">
                <div className="auth-step auth-step-done">
                  <div className="auth-step-icon">✓</div>
                  <div className="auth-step-content">
                    <h4>Your account is ready</h4>
                    <p>
                      You were invited by your workspace admin, so there's no
                      approval wait — you can sign in right away.
                    </p>
                  </div>
                </div>
              </div>
              <Link to="/login" className="auth-button auth-button-primary">
                Sign in
              </Link>
            </div>
          ) : signupSuccess ? (
            <div className="auth-success-flow">
              <div className="auth-success-steps">
                <div className="auth-step auth-step-done">
                  <div className="auth-step-icon">✓</div>
                  <div className="auth-step-content">
                    <h4>Account created</h4>
                    <p>Your account has been created successfully</p>
                  </div>
                </div>
                <div className="auth-step auth-step-active">
                  <div className="auth-step-icon">2</div>
                  <div className="auth-step-content">
                    <h4>Verify your email</h4>
                    <p>
                      We sent a verification link to{" "}
                      <strong>{signupEmail}</strong>. Click it to verify your
                      address.
                    </p>
                  </div>
                </div>
                <div className="auth-step auth-step-pending">
                  <div className="auth-step-icon">3</div>
                  <div className="auth-step-content">
                    <h4>Account approval</h4>
                    <p>
                      Once verified, our team will review and approve your
                      account within 1 business day.
                    </p>
                  </div>
                </div>
              </div>
              <p className="auth-success-note">
                Questions? Email{" "}
                <a href="mailto:support@stratusedge.ai">support@stratusedge.ai</a>
              </p>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="auth-form">
            {!inviteToken && (
            <div className="auth-row">
              <div className="auth-input-container">
                <label htmlFor="signup-company">Company Name</label>
                <input
                  id="signup-company"
                  type="text"
                  name="company_name"
                  placeholder="Company Name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  className="auth-input auth-input-lg"
                />
              </div>
            </div>
            )}

            <div className="auth-row">
              <div className="auth-input-container">
                <label htmlFor="signup-first-name">First Name</label>
                <input
                  id="signup-first-name"
                  type="text"
                  name="first_name"
                  placeholder="First Name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  className="auth-input auth-input-lg"
                />
              </div>
              <div className="auth-input-container">
                <label htmlFor="signup-last-name">Last Name</label>
                <input
                  id="signup-last-name"
                  type="text"
                  name="last_name"
                  placeholder="Last Name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  className="auth-input auth-input-lg"
                />
              </div>
            </div>

            {!inviteToken && (
            <div className="auth-row">
              <div className="auth-input-container">
                <label htmlFor="signup-email">Email Address</label>
                <input
                  id="signup-email"
                  type="email"
                  name="email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="auth-input auth-input-lg"
                  autoComplete="email"
                />
              </div>
            </div>
            )}

            <div className="auth-row">
              <div className="auth-input-container">
                <label htmlFor="signup-password">Password</label>
                <div className="relative">
                  <input
                    id="signup-password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="auth-input auth-input-lg password-input"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="auth-password-toggle"
                    onClick={togglePasswordVisibility}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <i className={showPassword ? "bi bi-eye" : "bi bi-eye-slash"}></i>
                  </button>
                </div>
              </div>
            </div>

            <div className="auth-checkbox-container">
              <input
                type="checkbox"
                id="terms"
                checked={isChecked}
                onChange={() => setIsChecked(!isChecked)}
              />
              <label htmlFor="terms" className="auth-checkbox-label">
                By signing up, you agree to our
                <button
                  type="button"
                  onClick={() => openModal("terms")}
                  className="auth-link-button"
                  aria-label="View Terms of Service"
                >
                  Terms
                </button>{" "}
                and have read our
                <button
                  type="button"
                  onClick={() => openModal("privacy")}
                  className="auth-link-button"
                  aria-label="View Privacy Policy"
                >
                  Privacy Policy
                </button>
              </label>
            </div>

            <button type="submit" className="auth-button auth-button-primary" disabled={loading || modalContent}>
              {loading ? (
                <span className="auth-button-loading">
                  <span className="auth-spinner" aria-hidden="true" />
                  Creating…
                </span>
              ) : (
                "Create account"
              )}
            </button>
          </form>
          )}

          {!signupSuccess && (
          <div className="auth-footer">
            <span>Already have an account?</span>{" "}
            <Link to="/login" className="auth-link-inline">Sign in</Link>
          </div>
          )}
        </div>
      </div>

      {modalContent && (
        <div className="auth-modal-overlay">
          <div className="auth-modal-content">
            <button
              onClick={closeModal}
              className="auth-modal-close-button"
              aria-label="Close modal"
            >
              ✕
            </button>
            <div className="auth-modal-body">
              {modalContent === "terms" ? <Terms /> : <Privacy />}
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default Signup;
// import React, { useState } from "react";
// import { Link, useNavigate } from "react-router-dom";
// import { toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import Terms from "./terms";
// import Privacy from "./privacy_policy";
// import './AuthPage.css';

// const Signup = () => {
//   const navigate = useNavigate();
//   const [formData, setFormData] = useState({
//     company_name: "",
//     first_name: "",
//     last_name: "",
//     email: "",
//     password: "",
//   });
//   const [isChecked, setIsChecked] = useState(false);
//   const [loading, setLoading] = useState(false);
//   const [modalContent, setModalContent] = useState(null);

//   const handleChange = (e) => {
//     setFormData({ ...formData, [e.target.name]: e.target.value });
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!isChecked) {
//       toast.error("You must agree to the terms and privacy policy.");
//       return;
//     }

//     setLoading(true);
//     const url = "https://api.stratusedge.ai/apis/register/";
//     const body = JSON.stringify(formData);

//     try {
//       const response = await fetch(url, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body,
//       });
//       const data = await response.json();

//       if (response.ok) {
//         toast.success("Account created! Check your email for verification.");
//         setTimeout(() => navigate("/login"), 3000);
//       } else {
//         const errorMessage = data.error || Object.values(data).flat().join(" ") || "Signup failed. Please try again.";
//         toast.error(errorMessage);
//       }
//     } catch (error) {
//       toast.error(error.message || "An error occurred. Please try again.");
//     }

//     setLoading(false);
//   };

//   const openModal = (content) => {
//     setModalContent(content);
//   };

//   const closeModal = () => {
//     setModalContent(null);
//   };

//   return (
//     <div className="auth-container">
//       <div className="auth-box">
//         <img src="/StratusEdgeFinal_whiteupscaled.png" className="auth-logo" alt="Logo" />
//         <h2 className="auth-title">Create Account</h2>
//         <p className="auth-subtitle">Join us by creating an account below.</p>

//         <form onSubmit={handleSubmit} className="auth-form">
//           <div className="auth-row">
//             <div className="auth-input-container">
//               <label>Company Name</label>
//               <input
//                 type="text"
//                 name="company_name"
//                 placeholder="Company Name"
//                 value={formData.company_name}
//                 onChange={handleChange}
//                 required
//                 className="auth-input"
//               />
//             </div>
//           </div>

//           <div className="auth-row">
//             <div className="auth-input-container">
//               <label>First Name</label>
//               <input
//                 type="text"
//                 name="first_name"
//                 placeholder="First Name"
//                 value={formData.first_name}
//                 onChange={handleChange}
//                 required
//                 className="auth-input"
//               />
//             </div>
//             <div className="auth-input-container">
//               <label>Last Name</label>
//               <input
//                 type="text"
//                 name="last_name"
//                 placeholder="Last Name"
//                 value={formData.last_name}
//                 onChange={handleChange}
//                 required
//                 className="auth-input"
//               />
//             </div>
//           </div>

//           <div className="auth-row">
//             <div className="auth-input-container">
//               <label>Email Address</label>
//               <input
//                 type="email"
//                 name="email"
//                 placeholder="you@example.com"
//                 value={formData.email}
//                 onChange={handleChange}
//                 required
//                 className="auth-input"
//               />
//             </div>
//             <div className="auth-input-container">
//               <label>Password</label>
//               <input
//                 type="password"
//                 name="password"
//                 placeholder="••••••••"
//                 value={formData.password}
//                 onChange={handleChange}
//                 required
//                 className="auth-input"
//               />
//             </div>
//           </div>

//           <div className="auth-checkbox-container">
//             <input
//               type="checkbox"
//               id="terms"
//               checked={isChecked}
//               onChange={() => setIsChecked(!isChecked)}
//             />
//             <label htmlFor="terms" className="auth-checkbox-label">
//               By signing up, you agree to our
//               <button
//                 type="button"
//                 onClick={() => openModal("terms")}
//                 className="auth-link-button"
//                 aria-label="View Terms of Service"
//               >
//                 Terms
//               </button>{" "}
//               and have read our
//               <button
//                 type="button"
//                 onClick={() => openModal("privacy")}
//                 className="auth-link-button"
//                 aria-label="View Privacy Policy"
//               >
//                 Privacy Policy
//               </button>
//             </label>
//           </div>

//           <button type="submit" className="auth-button" disabled={loading || modalContent}>
//             {loading ? "Processing..." : "Sign Up"}
//           </button>
//         </form>

//         <p className="auth-toggle">
//           Already have an account? <Link to="/login" className="auth-link">Login</Link>
//         </p>
//       </div>

//       {/* Modal for Terms or Privacy */}
//       {modalContent && (
//         <div className="auth-modal-overlay">
//           <div className="auth-modal-content">
//             <button
//               onClick={closeModal}
//               className="auth-modal-close-button"
//               aria-label="Close modal"
//             >
//               ✕
//             </button>
//             <div className="auth-modal-body">
//               {modalContent === "terms" ? <Terms /> : <Privacy />}
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default Signup;
